import { APIEntityType } from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { type I18nLocale, Trans, t } from '@ardoq/i18n';
import { FlexBox, Stack } from '@ardoq/layout';
import { ComponentIconRepresentation } from '@ardoq/renderers';
import { InlineText, Text } from '@ardoq/typography';
import { ReactNode } from 'react';
import { graphics } from '../CellRenderers/atoms';
import { ExtendedRowNode } from '../types';

const MAX_LISTED_COMPONENTS = 50;

const getChildCount = (node: ExtendedRowNode): number =>
  (node.data as { childCount?: number } | undefined)?.childCount ?? 0;

const getDefaultIntroText = (count: number): string => {
  if (count === 1) {
    return 'This component will be moved under <bold>{{targetName}}</bold>. Its descendants stay attached to it.';
  }
  return 'These {{count}} components will be moved under <bold>{{targetName}}</bold>. Their descendants stay attached to them.';
};

const getIntroText = (count: number, targetName: string): ReactNode => (
  <Trans
    key="moved-components-intro"
    i18nKey="inventory:moveComponents.text"
    defaults={getDefaultIntroText(count)}
    values={{ count, targetName }}
    components={{ bold: <InlineText variant="text2Bold">{''}</InlineText> }}
  />
);

const getRemainingText = (i18nLocale: I18nLocale, count: number): string =>
  t(
    i18nLocale,
    'inventory:moveComponents.remainingComponents',
    { count },
    '+ {{count}} more'
  );

const MovedComponentRow = ({
  node,
  scopeData,
}: {
  node: ExtendedRowNode;
  scopeData: EnhancedScopeData;
}): ReactNode => {
  const data = node.data;
  if (!data) {
    return null;
  }
  const childCount = getChildCount(node);
  return (
    <FlexBox gap="small" align="center">
      <ComponentIconRepresentation
        entityType={APIEntityType.COMPONENT}
        entityId={data._id}
        enhancedScopeData={scopeData}
        graphics={graphics}
      />
      <Text variant="text2">
        {data.name}
        {childCount > 0 ? ` (${childCount})` : ''}
      </Text>
    </FlexBox>
  );
};

export const getMoveComponentsDialogText = ({
  movedNodes,
  targetName,
  scopeData,
  i18nLocale,
}: {
  movedNodes: ExtendedRowNode[];
  targetName: string;
  scopeData: EnhancedScopeData;
  i18nLocale: I18nLocale;
}): ReactNode[] => {
  const listedNodes = movedNodes.slice(0, MAX_LISTED_COMPONENTS);
  const remainingCount = movedNodes.length - listedNodes.length;
  return [
    getIntroText(movedNodes.length, targetName),
    <Stack gap="small" key="moved-components-list">
      {listedNodes.map(node => (
        <MovedComponentRow
          key={node.data?._id}
          node={node}
          scopeData={scopeData}
        />
      ))}
      {remainingCount > 0 && (
        <Text variant="text2" color="textSubtle">
          {getRemainingText(i18nLocale, remainingCount)}
        </Text>
      )}
    </Stack>,
  ];
};
